import {
  ContactCompany,
  ContactCompanyActivity,
  ContactCompanyActivityType,
  ContactCompanyMember,
  ContactCompanyOwnerId,
  ContactCompanyRole,
  normalizeContactCompanyDropoffLocationIds,
  normalizeContactCompanyTerminalIds,
  type CrmActivityDomain,
} from '@otw/models/leads/contactCompanies'
import { db } from '@otw/models/db'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { addDoc, deleteDoc, deleteField, doc, getDoc, getDocs, setDoc, updateDoc } from 'firebase/firestore'
import { useAuth } from 'contexts/AuthContext'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import {
  applyMergedCompanyNameToLead,
  buildMergedContactCompany,
  type MergeContactCompaniesFieldSources,
  type MergeContactCompaniesInput,
} from 'views/SuperAdmin/Management/LeadManagement/util/mergeContactCompanies'

export const contactCompaniesQueryKey = ['contact_companies'] as const

export interface CreateContactCompanyInput {
  name: string
  website?: string
  notes?: string
  ownerId: ContactCompanyOwnerId
  businessAddressId?: string | null
  dropoffLocationIds?: string[]
  terminalIds?: string[]
  members?: ContactCompanyMember[]
  /** Optional override for the initial activity timestamp; defaults to `null`. */
  lastActivityAt?: Date | null
}

export interface UpdateContactCompanyInput {
  id: string
  name: string
  website?: string
  notes?: string
  ownerId: ContactCompanyOwnerId
  businessAddressId?: string | null
  dropoffLocationIds?: string[]
  terminalIds?: string[]
  members?: ContactCompanyMember[]
  /** When provided, replaces the company's `lastActivityAt`. */
  lastActivityAt?: Date | null
}

export interface LogContactCompanyActivityInput {
  companyId: string
  type: ContactCompanyActivityType
  /** Free-text body. For `task` activities this is the task title. */
  note: string
  /** Linked task doc id when {@link type} is `'task'`. */
  taskId?: string
  /** Linked lead doc id when {@link type} is `'lead'`. */
  leadId?: string
  /** Linked contact doc id when the activity is tied to a member contact. */
  contactId?: string
  /** Sales vs inventory; defaults to sales when omitted. */
  domain?: CrmActivityDomain
}

export interface AddContactCompanyMemberInput {
  companyId: string
  contactId: string
  role?: ContactCompanyRole
}

export interface UpdateContactCompanyMemberRoleInput {
  companyId: string
  contactId: string
  role: ContactCompanyRole
}

export interface RemoveContactCompanyMemberInput {
  companyId: string
  contactId: string
}

export interface AddContactCompanyTerminalInput {
  companyId: string
  terminalId: string
}

export interface RemoveContactCompanyTerminalInput {
  companyId: string
  terminalId: string
}

export type {
  MergeContactCompaniesFieldSources,
  MergeContactCompaniesInput,
} from 'views/SuperAdmin/Management/LeadManagement/util/mergeContactCompanies'

const sortCompaniesForList = (companies: ContactCompany[]): ContactCompany[] =>
  [...companies].sort((a, b) => {
    const aMs = a.lastActivityAt?.getTime() ?? a.updatedAt.getTime()
    const bMs = b.lastActivityAt?.getTime() ?? b.updatedAt.getTime()
    return bMs - aMs
  })

type UseContactCompaniesOptions = {
  /** When false, skip the full companies list query (mutations only). Default true. */
  fetchList?: boolean
}

export const useContactCompanies = (options?: UseContactCompaniesOptions) => {
  const fetchList = options?.fetchList !== false
  const { userInfo } = useAuth()
  const { showSuccess, showError } = useSnackbar()
  const queryClient = useQueryClient()

  const {
    data: companies,
    isLoading,
    error,
  } = useQuery({
    queryKey: contactCompaniesQueryKey,
    queryFn: async () => {
      const snapshot = await getDocs(db.contact_companies)
      return sortCompaniesForList(snapshot.docs.map((d) => d.data()))
    },
    enabled: fetchList,
    staleTime: 1000 * 60 * 5,
  })

  const createMutation = useMutation({
    mutationFn: async (input: CreateContactCompanyInput) => {
      const uid = userInfo?.id
      if (!uid) throw new Error('You must be signed in.')
      const now = new Date()
      const payload: ContactCompany = {
        id: '',
        name: input.name.trim(),
        ...(input.website?.trim() ? { website: input.website.trim() } : {}),
        ...(input.notes?.trim() ? { notes: input.notes.trim() } : {}),
        ownerId: input.ownerId ?? null,
        ...(input.businessAddressId ? { businessAddressId: input.businessAddressId } : {}),
        dropoffLocationIds: normalizeContactCompanyDropoffLocationIds(
          input.dropoffLocationIds,
        ),
        terminalIds: normalizeContactCompanyTerminalIds(input.terminalIds),
        members: input.members ?? [],
        lastActivityAt: input.lastActivityAt === undefined ? null : input.lastActivityAt,
        activities: [],
        createdAt: now,
        updatedAt: now,
        createdByUserId: uid,
      }
      const docRef = await addDoc(db.contact_companies, payload)
      return docRef.id
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: contactCompaniesQueryKey })
      showSuccess('Company created')
    },
    onError: () => showError('Could not create company'),
  })

  const updateMutation = useMutation({
    mutationFn: async (input: UpdateContactCompanyInput) => {
      const ref = doc(db.contact_companies, input.id)
      const snap = await getDoc(ref)
      const current = snap.data()
      if (!current) throw new Error('Company not found')
      const merged: ContactCompany = {
        ...current,
        name: input.name.trim(),
        ownerId: input.ownerId ?? null,
        updatedAt: new Date(),
        ...(input.lastActivityAt !== undefined
          ? { lastActivityAt: input.lastActivityAt }
          : {}),
        ...(input.members !== undefined ? { members: [...input.members] } : {}),
      }
      if (input.website?.trim()) merged.website = input.website.trim()
      else delete merged.website
      if (input.notes?.trim()) merged.notes = input.notes.trim()
      else delete merged.notes
      if (input.businessAddressId?.trim()) {
        merged.businessAddressId = input.businessAddressId.trim()
      } else {
        delete merged.businessAddressId
      }
      merged.dropoffLocationIds = normalizeContactCompanyDropoffLocationIds(
        input.dropoffLocationIds,
      )
      if (input.terminalIds !== undefined) {
        merged.terminalIds = normalizeContactCompanyTerminalIds(input.terminalIds)
      } else if (!merged.terminalIds) {
        merged.terminalIds = []
      }
      delete (merged as ContactCompany & { businessAddress?: unknown }).businessAddress
      delete (merged as ContactCompany & { dropoffLocation?: unknown }).dropoffLocation
      delete (merged as ContactCompany & { dropoffLocationId?: unknown }).dropoffLocationId
      await setDoc(ref, merged)
      await updateDoc(ref, {
        businessAddress: deleteField(),
        dropoffLocation: deleteField(),
        dropoffLocationId: deleteField(),
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: contactCompaniesQueryKey })
      showSuccess('Company updated')
    },
    onError: () => showError('Could not update company'),
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await deleteDoc(doc(db.contact_companies, id))
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: contactCompaniesQueryKey })
      showSuccess('Company deleted')
    },
    onError: () => showError('Could not delete company'),
  })

  const logActivityMutation = useMutation({
    mutationFn: async (input: LogContactCompanyActivityInput) => {
      const uid = userInfo?.id
      if (!uid) throw new Error('You must be signed in.')
      const ref = doc(db.contact_companies, input.companyId)
      const snap = await getDoc(ref)
      const current = snap.data()
      if (!current) throw new Error('Company not found')
      const now = new Date()
      const newActivity: ContactCompanyActivity = {
        type: input.type,
        note: input.note?.trim() ?? '',
        ...(input.taskId ? { taskId: input.taskId } : {}),
        ...(input.leadId ? { leadId: input.leadId } : {}),
        ...(input.contactId ? { contactId: input.contactId } : {}),
        domain: input.domain === 'inventory' ? 'inventory' : 'sales',
        createdAt: now,
        createdByUserId: uid,
      }
      const merged: ContactCompany = {
        ...current,
        activities: [...(current.activities ?? []), newActivity],
        lastActivityAt: now,
        updatedAt: now,
      }
      await setDoc(ref, merged)
      return { companyId: input.companyId, company: merged, activity: newActivity }
    },
    onSuccess: (result) => {
      // Same pattern as contacts: do not await a full-collection refetch on Save.
      queryClient.setQueryData<ContactCompany[]>(contactCompaniesQueryKey, (prev) => {
        if (!prev) return prev
        return prev
          .map((c) => (c.id === result.companyId ? result.company : c))
          .sort((a, b) => {
            const aMs = a.lastActivityAt?.getTime() ?? a.updatedAt.getTime()
            const bMs = b.lastActivityAt?.getTime() ?? b.updatedAt.getTime()
            return bMs - aMs
          })
      })
      queryClient.setQueryData<ContactCompany | null>(
        ['contact_companies', result.companyId],
        result.company,
      )
      void queryClient.invalidateQueries({ queryKey: contactCompaniesQueryKey, exact: true })
      showSuccess('Activity logged')
    },
    onError: () => showError('Could not log activity'),
  })

  const addMemberMutation = useMutation({
    mutationFn: async (input: AddContactCompanyMemberInput) => {
      const ref = doc(db.contact_companies, input.companyId)
      const snap = await getDoc(ref)
      const current = snap.data()
      if (!current) throw new Error('Company not found')
      const trimmedContactId = input.contactId.trim()
      if (!trimmedContactId) throw new Error('Invalid contact id')
      const existing = current.members.find((m) => m.contactId === trimmedContactId)
      if (existing) {
        if (input.role && existing.role !== input.role) {
          const merged: ContactCompany = {
            ...current,
            members: current.members.map((m) =>
              m.contactId === trimmedContactId ? { ...m, role: input.role! } : m,
            ),
            updatedAt: new Date(),
          }
          await setDoc(ref, merged)
        }
        return
      }
      const member: ContactCompanyMember = {
        contactId: trimmedContactId,
        role: input.role ?? 'manager',
        addedAt: new Date(),
      }
      const merged: ContactCompany = {
        ...current,
        members: [...current.members, member],
        updatedAt: new Date(),
      }
      await setDoc(ref, merged)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: contactCompaniesQueryKey })
      showSuccess('Contact added to company')
    },
    onError: () => showError('Could not add contact to company'),
  })

  const updateMemberRoleMutation = useMutation({
    mutationFn: async (input: UpdateContactCompanyMemberRoleInput) => {
      const ref = doc(db.contact_companies, input.companyId)
      const snap = await getDoc(ref)
      const current = snap.data()
      if (!current) throw new Error('Company not found')
      const merged: ContactCompany = {
        ...current,
        members: current.members.map((m) =>
          m.contactId === input.contactId ? { ...m, role: input.role } : m,
        ),
        updatedAt: new Date(),
      }
      await setDoc(ref, merged)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: contactCompaniesQueryKey })
      showSuccess('Role updated')
    },
    onError: () => showError('Could not update role'),
  })

  const mergeMutation = useMutation({
    mutationFn: async (input: MergeContactCompaniesInput) => {
      if (input.primaryCompanyId === input.secondaryCompanyId) {
        throw new Error('Choose two different companies to merge.')
      }

      const primaryRef = doc(db.contact_companies, input.primaryCompanyId)
      const secondaryRef = doc(db.contact_companies, input.secondaryCompanyId)
      const [primarySnap, secondarySnap] = await Promise.all([
        getDoc(primaryRef),
        getDoc(secondaryRef),
      ])
      const primary = primarySnap.data()
      const secondary = secondarySnap.data()
      if (!primary) throw new Error('Primary company not found')
      if (!secondary) throw new Error('Secondary company not found')

      const merged = buildMergedContactCompany(primary, secondary, input.fieldSources)
      const secondaryName = secondary.name.trim()
      const mergedName = merged.name.trim()

      await setDoc(primaryRef, merged)
      await updateDoc(primaryRef, {
        businessAddress: deleteField(),
        dropoffLocation: deleteField(),
        dropoffLocationId: deleteField(),
      })

      const [leadsSnapshot] = await Promise.all([getDocs(db.leads)])

      await Promise.all(
        leadsSnapshot.docs.map(async (leadDoc) => {
          const lead = leadDoc.data()
          const updated = applyMergedCompanyNameToLead(lead, secondaryName, mergedName)
          if (!updated) return
          await updateDoc(leadDoc.ref, { company: updated.company, updatedAt: new Date() })
        }),
      )

      await deleteDoc(secondaryRef)
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: contactCompaniesQueryKey }),
        queryClient.invalidateQueries({ queryKey: ['leads'] }),
      ])
      showSuccess('Companies merged')
    },
    onError: (error: Error) => {
      showError(error.message || 'Could not merge companies')
    },
  })

  const addTerminalMutation = useMutation({
    mutationFn: async (input: AddContactCompanyTerminalInput) => {
      const ref = doc(db.contact_companies, input.companyId)
      const snap = await getDoc(ref)
      const current = snap.data()
      if (!current) throw new Error('Company not found')
      const terminalId = input.terminalId.trim()
      if (!terminalId) throw new Error('Invalid terminal id')
      const terminalIds = normalizeContactCompanyTerminalIds([
        ...(current.terminalIds ?? []),
        terminalId,
      ])
      if (terminalIds.length === (current.terminalIds ?? []).length) return
      await setDoc(ref, {
        ...current,
        terminalIds,
        updatedAt: new Date(),
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: contactCompaniesQueryKey })
      showSuccess('Terminal associated')
    },
    onError: () => showError('Could not associate terminal'),
  })

  const removeTerminalMutation = useMutation({
    mutationFn: async (input: RemoveContactCompanyTerminalInput) => {
      const ref = doc(db.contact_companies, input.companyId)
      const snap = await getDoc(ref)
      const current = snap.data()
      if (!current) throw new Error('Company not found')
      const terminalId = input.terminalId.trim()
      const terminalIds = normalizeContactCompanyTerminalIds(current.terminalIds).filter(
        (id) => id !== terminalId,
      )
      if (terminalIds.length === (current.terminalIds ?? []).length) return
      await setDoc(ref, {
        ...current,
        terminalIds,
        updatedAt: new Date(),
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: contactCompaniesQueryKey })
      showSuccess('Terminal removed from company')
    },
    onError: () => showError('Could not remove terminal'),
  })

  const removeMemberMutation = useMutation({
    mutationFn: async (input: RemoveContactCompanyMemberInput) => {
      const ref = doc(db.contact_companies, input.companyId)
      const snap = await getDoc(ref)
      const current = snap.data()
      if (!current) throw new Error('Company not found')
      const merged: ContactCompany = {
        ...current,
        members: current.members.filter((m) => m.contactId !== input.contactId),
        updatedAt: new Date(),
      }
      await setDoc(ref, merged)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: contactCompaniesQueryKey })
      showSuccess('Contact removed from company')
    },
    onError: () => showError('Could not remove contact'),
  })

  return {
    companies: companies ?? [],
    isLoading,
    error: error instanceof Error ? error : error ? new Error(String(error)) : null,
    createCompany: (input: CreateContactCompanyInput) => createMutation.mutateAsync(input),
    updateCompany: (input: UpdateContactCompanyInput) => updateMutation.mutateAsync(input),
    deleteCompany: (id: string) => deleteMutation.mutateAsync(id),
    logCompanyActivity: (input: LogContactCompanyActivityInput) =>
      logActivityMutation.mutateAsync(input),
    addCompanyMember: (input: AddContactCompanyMemberInput) =>
      addMemberMutation.mutateAsync(input),
    updateCompanyMemberRole: (input: UpdateContactCompanyMemberRoleInput) =>
      updateMemberRoleMutation.mutateAsync(input),
    removeCompanyMember: (input: RemoveContactCompanyMemberInput) =>
      removeMemberMutation.mutateAsync(input),
    addCompanyTerminal: (input: AddContactCompanyTerminalInput) =>
      addTerminalMutation.mutateAsync(input),
    removeCompanyTerminal: (input: RemoveContactCompanyTerminalInput) =>
      removeTerminalMutation.mutateAsync(input),
    mergeCompanies: (input: MergeContactCompaniesInput) => mergeMutation.mutateAsync(input),
    isSaving: createMutation.isPending || updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isMerging: mergeMutation.isPending,
    isLoggingActivity: logActivityMutation.isPending,
    isMutatingMembers:
      addMemberMutation.isPending ||
      updateMemberRoleMutation.isPending ||
      removeMemberMutation.isPending,
    isMutatingTerminals:
      addTerminalMutation.isPending || removeTerminalMutation.isPending,
  }
}
